import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, X, Loader2, Bot, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { modifyScreen } from '../services/aiService';
import { toast } from 'sonner';
import { useSubscription } from '../contexts/SubscriptionContext';

const AIChatPanel = ({ screen, onUpdateScreen, onClose, supabase, projectId }) => {
    const { trackAIUsage } = useSubscription();
    const [messages, setMessages] = useState([
        { role: 'assistant', content: 'Hi! I can help you edit this screen. What would you like to change?' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage = input;
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsLoading(true);

        try {
            // Context includes current screen elements and name
            const context = {
                screenName: screen.name,
                elementCount: screen.elements.length
            };

            // Pass supabase client instead of apiKey
            const updatedScreen = await modifyScreen(supabase, screen, userMessage, context);

            onUpdateScreen(updatedScreen);

            // Track AI modification usage
            await trackAIUsage('modification', projectId);

            setMessages(prev => [...prev, { role: 'assistant', content: 'I\'ve updated the screen based on your request.' }]);
            toast.success('Screen updated by AI');
        } catch (error) {
            console.error('AI Error:', error);
            setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, I encountered an error: ${error.message}` }]);
            toast.error('Failed to update screen');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full border-l bg-card w-80 shadow-xl absolute right-[320px] top-0 bottom-0 z-20 animate-in slide-in-from-right-10 duration-200">
            <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-500" />
                    <h3 className="font-semibold">AI Assistant</h3>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
                    <X className="h-4 w-4" />
                </Button>
            </div>

            <ScrollArea className="flex-1 p-4">
                <div className="space-y-4" ref={scrollRef}>
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-purple-100 text-purple-600'}`}>
                                {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                            </div>
                            <div className={`rounded-lg p-3 text-sm max-w-[80%] ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                                {msg.content}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex gap-2">
                            <div className="h-8 w-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
                                <Bot className="h-4 w-4" />
                            </div>
                            <div className="bg-muted rounded-lg p-3 flex items-center">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            </div>
                        </div>
                    )}
                </div>
            </ScrollArea>

            <div className="p-4 border-t bg-card">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSend();
                    }}
                    className="flex gap-2"
                >
                    <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Ask AI to change something..."
                        disabled={isLoading}
                        className="flex-1"
                    />
                    <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
                        <Send className="h-4 w-4" />
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default AIChatPanel;
